import type { CreateUploadTaskReq } from "../../shared/api";
import { PLATFORM_VK } from "../../shared/api";
import type { FormState, VideoData, StructuredDescription } from "./types";

export const ACCESS_PUBLIC = 'public/members';
export const ACCESS_LINK = 'link';
/** playlist_ref value for a deliberate "publish without a playlist" (empty string = not chosen yet). */
export const PLAYLIST_NONE = '__none__';

export const DEFAULT_FORM_STATE: FormState = {
  path: '', thumbnail_path: '', title: '', description: '', platforms: [],
};

export const DEFAULT_VIDEO_DATA: VideoData = {
  subjectName: '', lectureNumber: 1, lectureName: '',
};

export const DEFAULT_STRUCTURED_DESC: StructuredDescription = {
  additionalMessage: '', timestamps: '',
  metadata: { date: '', lecturer: '' },
  staff: { operator: '', operatorId: null, editor: '', editorId: null },
  references: { youtube: '', vk: '' },
};

function checkDate(v: string): string | null {
  if (!/^\d{2}\.\d{2}\.\d{2}$/.test(v)) return 'Неверный формат, ожидается ДД.ММ.ГГ';
  const [d, m, y] = v.split('.').map(Number);
  const date = new Date(2000 + y, m - 1, d);
  if (date.getFullYear() !== 2000 + y || date.getMonth() !== m - 1 || date.getDate() !== d) return 'Некорректная дата';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return date > today ? 'Дата находится в будущем' : null;
}

export function validateStructuredDescription(
  data: StructuredDescription & { title: string; subjectName?: string; lectureName?: string }
): Record<string, string[]> {
  const warnings: Record<string, string[]> = {};
  const add = (key: string, msg: string | null) => { if (msg) (warnings[key] = warnings[key] || []).push(msg); };

  const noDot = (v?: string) => (v?.trimEnd().endsWith('.') ? 'Не должно заканчиваться точкой' : null);
  const checkCaps = (v?: string) => (v && v.trim()[0] !== v.trim()[0].toUpperCase() ? 'Имя должно начинаться с большой буквы' : null);

  add('title', noDot(data.title));
  add('subjectName', noDot(data.subjectName));
  add('lectureName', noDot(data.lectureName));
  add('metadata.lecturer', checkCaps(data.metadata.lecturer));
  add('staff.operator', checkCaps(data.staff.operator));
  add('staff.editor', checkCaps(data.staff.editor));

  if (data.metadata.date.trim()) add('metadata.date', checkDate(data.metadata.date.trim()));
  if (data.references.youtube.trim()) {
    const y = data.references.youtube.trim();
    if (!/^https?:\/\//.test(y) || !y.includes('youtube.com/playlist')) add('references.youtube', 'Не похоже на ссылку на плейлист YouTube');
  }
  if (data.references.vk.trim()) {
    const v = data.references.vk.trim();
    if (!/^https?:\/\//.test(v) || (!v.includes('vk.com') && !v.includes('vkvideo.ru'))) add('references.vk', 'Не похоже на ссылку ВКонтакте');
  }
  if (data.timestamps.trim() && !data.timestamps.trim().startsWith('00:00:00')) {
    add('timestamps', 'Таймкод должен начинаться с 00:00:00');
  }
  return warnings;
}

export function generateTitle(videoData: VideoData): string {
  const parts: string[] = [];
  if (videoData.subjectName.trim()) parts.push(videoData.subjectName.trim());
  if (videoData.lectureNumber && parts.length > 0) parts.push(`${videoData.lectureNumber}.`);
  if (videoData.lectureName.trim()) parts.push(videoData.lectureName.trim());
  return parts.join(' ');
}

export function generateDescription(s: StructuredDescription): string {
  const group = (...lines: (string | null | undefined)[]) => lines.filter(Boolean).join('\n');
  return [
    s.additionalMessage.trim(),
    s.timestamps.trim() ? `Таймкоды:\n${s.timestamps.trim()}` : null,
    group(s.metadata.date.trim() && `Дата лекции: ${s.metadata.date.trim()}`, s.metadata.lecturer.trim() && `Лектор: ${s.metadata.lecturer.trim()}`),
    group(s.staff.operator.trim() && `Оператор: ${s.staff.operator.trim()}`, s.staff.editor.trim() && `Монтажёр: ${s.staff.editor.trim()}`),
    group(s.references.youtube.trim() && `Плейлист на YouTube: ${s.references.youtube.trim()}`, s.references.vk.trim() && `Плейлист в ВК: ${s.references.vk.trim()}`),
  ].filter(Boolean).join('\n\n');
}

export function validate(formState: FormState): ({ ok: true, tasks: CreateUploadTaskReq[] } | { ok: false, error: string }) {
  const { path, thumbnail_path, title, description, platforms } = formState;
  if (!platforms.length) return { ok: false, error: 'Выберите хотя бы одну платформу' };
  if (!path || !thumbnail_path || !title || !description) return { ok: false, error: 'Форма заполнена не полностью' };
  if (title.length > 100) return { ok: false, error: 'Название должно быть не длиннее 100 символов' };
  if (description.length > 2000) return { ok: false, error: 'Описание не должно превышать 2000 символов' };
  if (/[<>]/.test(description)) return { ok: false, error: 'Описание не должно содержать "<" или ">"' };

  for (const p of platforms) {
    if (!p.channel_ref) return { ok: false, error: 'Не указан канал для публикации' };
    if (!p.playlist_ref) return { ok: false, error: 'Не указан плейлист (если он не нужен, выберите «Без плейлиста»)' };
  }

  return {
    ok: true,
    tasks: platforms.map(p => ({
      path, thumbnail_path, platform: p.platform as any, title, description,
      channel_ref: p.channel_ref, playlist_ref: p.playlist_ref === PLAYLIST_NONE ? null : p.playlist_ref,
      public: p.platform === PLATFORM_VK ? true : p.access === ACCESS_PUBLIC,
    }))
  };
}
